<script setup lang="ts">
import type {
  FindManyFilter,
  FindManyFilterItem,
  FindManyFilterItemV2,
  FindManyFilterV2,
} from '@rialtic/types'
import { useArrayFilter, useArrayMap } from '@vueuse/core'
import { ref } from '#imports'
import type { FilterItemWithState } from '../../composables/filters'
import { useUrlParamsState } from '../../composables/urlParamsState'
import useSimpleSearch from '../../composables/useSimpleSearch'
import Button from '../Button.vue'
import FloatingUi from '../FloatingUi.vue'
import ListItemCheckbox from '../list/ItemCheckbox.vue'
import ListItemRadio from '../list/ItemRadio.vue'
import Search from '../Search.vue'
import FilterChip from './Chip.vue'

const props = defineProps<{
  filter: FindManyFilter | FindManyFilterV2
  search?: boolean
  selectAllDisabled?: boolean
  clearAllDisabled?: boolean
  filtersWithCodeFormat?: string[]
  showValue?: boolean
}>()

const { setUrlParams, getUrlParams, deleteUrlParam } = useUrlParamsState()

const emit = defineEmits([
  'filter-all',
  'filter-change',
  'filter-clear',
  'error',
])

const activator = ref(null)
const open = ref(false)
const searchInput = ref('')

const exclusiveFilterValues = computed(
  () =>
    props.filter.items?.reduce((values, item) => {
      if ((item as FindManyFilterItemV2).isExclusive) {
        values.push(item.value)
      }

      return values
    }, [] as string[]) ?? [],
)

const filtersWithActiveState = useArrayMap(
  () => props.filter.items || [],
  (item) =>
    ({
      ...item,
      active: getUrlParams(props.filter.name).includes(item.value.toString()),
    }) as FilterItemWithState<FindManyFilterItem | FindManyFilterItemV2>,
)

const activeFilterLabels = useArrayMap(
  useArrayFilter(filtersWithActiveState, ({ active }) => Boolean(active)),
  ({ label }) => label,
)

const filteredItems = useSimpleSearch<FilterItemWithState>(
  filtersWithActiveState,
  searchInput,
  ['label', 'value'],
)

const setOpen = (isOpen = false) => {
  open.value = isOpen
}

const removeUrlParamFilter = (paramName: string, paramValue: string) => {
  const params = getUrlParams(paramName)
  const revisedParams = params.filter((param) => param !== paramValue)

  if (!revisedParams.length) {
    deleteUrlParam(paramName)
  } else {
    setUrlParams(paramName, revisedParams)
  }
}

const filterChange = (
  item: FilterItemWithState,
  name: string,
  isMultiple = true,
) => {
  emit('filter-change', { item, name })

  if (item.active) {
    removeUrlParamFilter(name, item.value)
  } else if ((item as FindManyFilterItemV2).isExclusive) {
    setUrlParams(name, item.value)
  } else {
    let params: string | string[] = !isMultiple
      ? item.value.toString()
      : [...getUrlParams(name), item.value]

    if (Array.isArray(params)) {
      params = params.filter(
        (value) => !exclusiveFilterValues.value.includes(value),
      )
    }

    setUrlParams(name, params)
  }
}

const selectAllFilters = (paramName: string) => {
  emit('filter-all', paramName)

  setUrlParams(
    paramName,
    props.filter.items?.map((item) => item.value).join('|') || [],
  )
}

const clearAllFilters = (paramName: string) => {
  emit('filter-clear', paramName)

  deleteUrlParam(paramName)
}
</script>

<template>
  <div class="relative inline-block">
    <div ref="activator">
      <slot v-bind="{ open, setOpen }" name="activator">
        <FilterChip
          v-model="activeFilterLabels"
          class="h-full"
          :label="filter.label"
          button-border-radius="rounded-lg"
          @click="setOpen(true)"
        />
      </slot>
    </div>
    <FloatingUi
      v-if="open"
      class="bg-surface shadow-lg"
      :anchor="activator!"
      placement="bottom-start"
      @click-outside="open = false"
    >
      <template #default="{ maxDimensions }">
        <section class="min-w-54 flex flex-col" :style="maxDimensions">
          <header class="mx-3 border-b border-neutral-200">
            <div
              class="flex w-full items-center justify-between space-x-4 py-3"
            >
              <h3 class="h3 whitespace-nowrap text-neutral-900">
                {{ filter.label }}
              </h3>
              <button
                class="rounded-full p-1"
                aria-label="Close"
                data-testid="close-dropdown-button"
                @click="open = false"
              >
                <div class="i-ph-x-bold" />
              </button>
            </div>
            <div v-if="search" class="flex items-center justify-between pb-3">
              <Search
                v-model="searchInput"
                :focus="open"
                placeholder="Search..."
                type="search"
              />
            </div>
          </header>
          <div class="filter-item-container">
            <ul class="px-3 pb-3 pt-2">
              <li
                v-for="item in search ? filteredItems : filtersWithActiveState"
                :key="item.value"
              >
                <ListItemRadio
                  v-if="
                    filter.isMultiple === false ||
                    (filter as FindManyFilterV2).type === 'single'
                  "
                  :checked="!!item.active"
                  :data-testid="
                    'filter-item-' +
                    item.label.replaceAll(' ', '-').toLowerCase()
                  "
                  @click="filterChange(item, filter.name, filter.isMultiple)"
                >
                  {{ (item as FindManyFilterItem).display_name || item.label }}
                </ListItemRadio>
                <ListItemCheckbox
                  v-else
                  :checked="!!item.active"
                  :data-testid="
                    'filter-item-' +
                    item.label.replaceAll(' ', '-').toLowerCase()
                  "
                  @click="filterChange(item, filter.name, filter.isMultiple)"
                >
                  <template
                    v-if="
                      filtersWithCodeFormat?.includes(filter.name) &&
                      !item.value.startsWith('ALL_')
                    "
                  >
                    <div class="flex flex-col">
                      <div>{{ item.value }}</div>
                      <div class="text-sm text-neutral-600">
                        {{
                          item.label.split(' - ').slice(1).join(' - ') ||
                          item.label
                        }}
                      </div>
                    </div>
                  </template>
                  <template v-else>
                    <div class="flex flex-col gap-1">
                      <span>
                        {{
                          (item as FindManyFilterItem).display_name ||
                          item.label
                        }}
                      </span>
                      <span v-if="showValue" class="text-neutral-500">
                        {{ item.value }}
                      </span>
                    </div>
                  </template>
                </ListItemCheckbox>
              </li>
            </ul>
          </div>
          <footer
            v-if="!selectAllDisabled || !clearAllDisabled"
            class="mx-3 flex justify-end border-t border-neutral-200 py-3"
          >
            <Button
              v-if="!selectAllDisabled"
              class="px-2 text-sm"
              variant="text"
              @click="selectAllFilters(filter.name)"
            >
              Select all
            </Button>
            <Button
              v-if="!clearAllDisabled"
              class="px-2 text-sm"
              variant="text"
              @click="clearAllFilters(filter.name)"
            >
              Clear
            </Button>
          </footer>
        </section>
      </template>
    </FloatingUi>
  </div>
</template>
